/**
 * Plan Service
 * ------------
 * Fetches subscription plan definitions from the Lambda GET /plans endpoint.
 * The backend is the single source of truth for all plan data — prices, limits,
 * features. Nothing is hardcoded here.
 *
 * UI-only metadata (icons, highlight badge, CTA labels) lives here because it
 * is purely presentational and has no effect on feature gating.
 */

const API_BASE = import.meta.env.VITE_API_BASE;

// ─── UI-only metadata (not business logic) ───────────────────────────────────

/** Emoji icon shown next to each plan name. */
export const PLAN_ICONS = { free: '🌱', pro: '⭐', pro_plus: '💎', family: '👨‍👩‍👧‍👦' };

/** Which plan gets the "Most Popular" badge on plan-selection UIs. */
export const PLAN_HIGHLIGHT = 'pro_plus';

/** CTA button text on the payment / onboarding page. */
export const PLAN_CTAS = {
  free:     'Get started free',
  pro:      'Start Pro',
  pro_plus: 'Start Pro Plus',
  family:   'Start Family Plan',
};

// ─── In-memory cache ─────────────────────────────────────────────────────────

let _cache = null;      // planId-keyed map: { free: {...}, pro: {...}, ... }
let _cacheTs = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Format a plan's price for display, e.g. '£9.99' or '£0'. */
export function formatPrice(plan) {
  if (!plan?.price) return '£0';
  const symbol = plan.currency === 'GBP' ? '£' : (plan.currency || '£');
  return `${symbol}${plan.price}`;
}

/** Format a plan's billing period for display, e.g. 'per month'. */
export function formatPeriod(plan) {
  const map = { forever: 'forever', monthly: 'per month', annually: 'per year' };
  return map[plan?.billingPeriod] || plan?.billingPeriod || '';
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Return a planId-keyed map of plan documents from the API.
 * Uses in-memory TTL cache. Returns last cached value (or empty map) on error —
 * no hardcoded fallback; components should handle an empty/loading state.
 *
 * @param {boolean} [forceRefresh]
 * @returns {Promise<Record<string, object>>}
 */
export async function getPlans(forceRefresh = false) {
  const now = Date.now();
  if (!forceRefresh && _cache && (now - _cacheTs) < CACHE_TTL) {
    return _cache;
  }

  try {
    const res = await fetch(`${API_BASE}/plans`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const { plans } = await res.json();
    const map = {};
    for (const plan of (plans || [])) {
      map[plan.planId] = plan;
    }

    if (Object.keys(map).length > 0) {
      _cache = map;
      _cacheTs = now;
      return _cache;
    }
  } catch (e) {
    console.warn('[planService] Could not fetch plans:', e.message);
  }

  // Return last successful cache if available, otherwise empty map.
  // No hardcoded plan data — the backend is the source of truth.
  return _cache || {};
}
